// https://www.radix-ui.com/primitives/docs/components/dialog
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useEffect, type ReactNode } from "react";
import cx from "ui/cx";
import IconClose from "ui/icons/IconClose";
import styles from "./Drawer.module.scss";

export interface DrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Fired when the drawer requests to close (close button, Esc, or backdrop click). */
  onOpenChange: (open: boolean) => void;
  /** Title shown in the drawer header, beside the close button. */
  title?: ReactNode;
  /** Drawer body content. */
  children?: ReactNode;
  /** Optional slot for extra header actions, rendered between the title and close button. */
  titleActions?: ReactNode;
  /** One-off override for the content panel. */
  className?: string;
}

/**
 * A panel that slides in from the right over a dimmed backdrop. Product-agnostic — pass `title` and `children`.
 */
export default function Drawer({ open, onOpenChange, title, children, titleActions, className }: DrawerProps) {
  // Lock body scroll while open;
  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPrimitive.Portal>
        {/* Non-modal Radix renders no overlay, so provide our own backdrop. */}
        <div
          className={styles.overlay}
          data-testid="drawer-overlay"
          data-state={open ? "open" : "closed"}
          onClick={() => onOpenChange(false)}
        />
        <DialogPrimitive.Content
          className={cx(styles.content, className)}
          data-testid="drawer-content"
          // No descriptive body text; opt out of Radix's aria-describedby warning.
          aria-describedby={undefined}
          // Close only via backdrop, Esc, or the close button — don't dismiss on interaction
          // with layers (modals, selects, popovers) portalled from within the drawer.
          onInteractOutside={(event) => event.preventDefault()}
        >
          <div className={styles.header}>
            <DialogPrimitive.Title className={styles.title}>{title}</DialogPrimitive.Title>
            {titleActions}
            <DialogPrimitive.Close className={styles.close} aria-label="Close" data-testid="drawer-close">
              <IconClose size={18} />
            </DialogPrimitive.Close>
          </div>
          <div className={styles.body}>{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
